import { gql, useQuery } from '@apollo/client';
import React from 'react';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { BuildDetailsErrorItem } from '../buildCheckDetails/buildDetailsErrorItem/BuildDetailsErrorItem';

import { FilterBuildSuccess } from './filterBuildSuccess/FilterBuildSuccess';

export function FilterBuildCheckDetails() {
  const { currentFilterBuildCheckDetailsRoute } = useInternalGraphLinking();
  const { checkId, graphId } = useRouteParams(
    currentFilterBuildCheckDetailsRoute,
  );
  const { data, loading, error } = useQuery<
    GraphQLTypes.FilterBuildCheckDetailsQuery,
    GraphQLTypes.FilterBuildCheckDetailsQueryVariables
  >(
    gql`
      query FilterBuildCheckDetailsQuery($graphId: ID!, $checkId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            buildTask {
              id
              ... on FilterCheckTask {
                buildResult {
                  id
                  ... on FilterBuildCheckPassed {
                    supergraphSchemaHash
                  }
                  ... on FilterBuildCheckFailed {
                    errors {
                      message
                      failedStep
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: { graphId, checkId },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );
  const checkWorkflowTask = data?.service?.checkWorkflow?.buildTask;
  if (loading) {
    return <Loading />;
  } else if (error) {
    return (
      <div className="p-2">
        <ErrorMessage error={error} />
      </div>
    );
  } else if (checkWorkflowTask?.__typename !== 'FilterCheckTask') {
    return <NotFound />;
  }

  const buildErrors =
    checkWorkflowTask.buildResult?.__typename === 'FilterBuildCheckFailed'
      ? checkWorkflowTask.buildResult.errors
      : undefined;

  const builtSchemaHash =
    checkWorkflowTask.buildResult?.__typename === 'FilterBuildCheckPassed'
      ? checkWorkflowTask.buildResult.supergraphSchemaHash
      : undefined;

  if (builtSchemaHash) {
    return (
      <FilterBuildSuccess graphId={graphId} builtSchemaHash={builtSchemaHash} />
    );
  } else if (!isMinLengthArray(1, buildErrors)) {
    return <Loading />;
  }

  return (
    <>
      {buildErrors?.map((buildError, i) => (
        <BuildDetailsErrorItem
          // eslint-disable-next-line react/no-array-index-key
          key={`error-${i}`}
          error={{ message: buildError.message, code: buildError.failedStep }}
        />
      ))}
    </>
  );
}
